// Tela da equipe (professor/coordenador/master) pro sistema de formação de
// caráter: cadastro de Missões (só master/coordenador), fila de Confirmações
// (aprovar/rejeitar o que o aluno marcou) e Reconhecer (registrar um ato já
// confirmado, sem o aluno precisar iniciar nada). Ver carater.js no backend.
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Check, X, Award, Pencil, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import { usePermissao } from '../../hooks/usePermissao';
import { Card, Button, Input, Select, Modal } from '../../Components/ui';
import { useToast } from '../../Components/Feedback';
import { API_URL } from '../../api/config';
import { useApi } from '../../api/useApi';

const ORIGEM_LABEL = {
  missao: 'Missão de Caráter',
  diario: 'Diário de Semeadura',
  indicacao_colega: 'Indicação de colega',
};

// timeZone explícito: sem isso, a hora exibida seguiria o fuso configurado no
// SISTEMA do navegador (não o relógio, o fuso), que pode estar errado mesmo
// com a hora "certa" na tela — mesmo cuidado de agoraBrasil() no backend.
const formatarData = (iso) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });

function CaraterAdmin() {
  const { institutionId } = useInstitution();
  const { token, user } = useAuth();
  const { podeCriar, podeEditar } = usePermissao('/carater');
  const queryClient = useQueryClient();
  const toast = useToast();

  const podeGerenciarMissoes = ['master', 'coordenador'].includes(user?.perfil);
  const [aba, setAba] = useState('pendentes');

  const authHeaders = { 'x-institution-id': institutionId, Authorization: `Bearer ${token}` };
  const api = useApi();

  const { data: principios = [] } = useQuery({
    queryKey: ['carater-principios'],
    queryFn: async () => {
      const res = await api.get(`/api/carater/principios`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token,
  });
  const nomePrincipio = (id) => principios.find((p) => p.id === id)?.nome || `Princípio ${id}`;

  const { data: missoes = [], isLoading: carregandoMissoes } = useQuery({
    queryKey: ['carater-missoes', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/carater/missoes`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token && aba === 'missoes',
  });

  const { data: pendentes = [], isLoading: carregandoPendentes } = useQuery({
    queryKey: ['carater-pendentes', institutionId],
    queryFn: async () => {
      const res = await api.get(`/api/carater/pendentes`);
      return res?.data ?? (Array.isArray(res) ? res : []);
    },
    enabled: !!institutionId && !!token && aba === 'pendentes',
  });

  const invalidarPendentes = () =>
    queryClient.invalidateQueries({ queryKey: ['carater-pendentes', institutionId] });

  // --- Modal de missão (criar/editar) ---
  const [modalMissaoAberto, setModalMissaoAberto] = useState(false);
  const [missaoEditando, setMissaoEditando] = useState(null);
  const [formMissao, setFormMissao] = useState({
    titulo: '',
    descricao: '',
    principio: '',
    ativa: true,
  });
  const [salvandoMissao, setSalvandoMissao] = useState(false);

  const abrirNovaMissao = () => {
    setMissaoEditando(null);
    setFormMissao({ titulo: '', descricao: '', principio: principios[0]?.id || '', ativa: true });
    setModalMissaoAberto(true);
  };

  const abrirEditarMissao = (missao) => {
    setMissaoEditando(missao);
    setFormMissao({
      titulo: missao.titulo,
      descricao: missao.descricao || '',
      principio: missao.principio,
      ativa: !!missao.ativa,
    });
    setModalMissaoAberto(true);
  };

  const salvarMissao = async (e) => {
    e.preventDefault();
    setSalvandoMissao(true);
    try {
      const url = missaoEditando
        ? `${API_URL}/api/carater/missoes/${missaoEditando.id}`
        : `${API_URL}/api/carater/missoes`;
      const res = await fetch(url, {
        method: missaoEditando ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(formMissao),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar missão');
      toast.success(data?.message || (missaoEditando ? 'Missão atualizada.' : 'Missão criada.'));
      setModalMissaoAberto(false);
      queryClient.invalidateQueries({ queryKey: ['carater-missoes', institutionId] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSalvandoMissao(false);
    }
  };

  const alternarAtiva = async (missao) => {
    try {
      const res = await fetch(`${API_URL}/api/carater/missoes/${missao.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          titulo: missao.titulo,
          descricao: missao.descricao,
          principio: missao.principio,
          ativa: !missao.ativa,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Erro ao atualizar missão');
      toast.success(data?.message || 'Missão atualizada com sucesso.');
      queryClient.invalidateQueries({ queryKey: ['carater-missoes', institutionId] });
    } catch (err) {
      toast.error(err.message);
    }
  };

  // --- Ações da fila de confirmação ---
  const [comentarios, setComentarios] = useState({});
  const [processando, setProcessando] = useState(null);

  const confirmarAto = async (id, aprovado) => {
    setProcessando(id);
    try {
      const res = await fetch(`${API_URL}/api/carater/${id}/confirmar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ aprovado, comentario: comentarios[id] || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao processar');
      toast.success(data?.message || (aprovado ? 'Ato confirmado.' : 'Ato rejeitado.'));
      invalidarPendentes();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessando(null);
    }
  };

  // --- Reconhecer ---
  const [buscaAluno, setBuscaAluno] = useState('');
  const [alunoSelecionado, setAlunoSelecionado] = useState(null);
  const [formReconhecer, setFormReconhecer] = useState({ principio: '', texto: '' });
  const [enviandoReconhecimento, setEnviandoReconhecimento] = useState(false);

  const { data: opcoesAluno = [] } = useQuery({
    queryKey: ['carater-busca-aluno', institutionId, buscaAluno],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/alunos?nome=${encodeURIComponent(buscaAluno)}`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Erro ao buscar inscritos');
      const body = await res.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: aba === 'reconhecer' && buscaAluno.trim().length >= 2 && !alunoSelecionado,
  });

  const enviarReconhecimento = async (e) => {
    e.preventDefault();
    if (!alunoSelecionado) return toast.error('Selecione um inscrito.');
    setEnviandoReconhecimento(true);
    try {
      const res = await fetch(`${API_URL}/api/carater/reconhecer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          id_aluno: alunoSelecionado.id,
          principio: formReconhecer.principio,
          texto: formReconhecer.texto,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao registrar reconhecimento');
      toast.success(data?.message || `Reconhecimento registrado para ${alunoSelecionado.nome}.`);
      setAlunoSelecionado(null);
      setBuscaAluno('');
      setFormReconhecer({ principio: '', texto: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnviandoReconhecimento(false);
    }
  };

  const abas = [
    {
      id: 'pendentes',
      label: `Confirmações${pendentes.length > 0 ? ` (${pendentes.length})` : ''}`,
    },
    { id: 'reconhecer', label: 'Reconhecer' },
    ...(podeGerenciarMissoes ? [{ id: 'missoes', label: 'Missões' }] : []),
  ];

  return (
    <div className="min-h-screen w-full bg-[#F9FAFB] dark:bg-gray-950">
      <main className="p-4 max-w-2xl mx-auto space-y-4">
        <div className="inline-flex p-1 rounded-xl bg-gray-100 dark:bg-gray-800 gap-1">
          {abas.map((a) => (
            <button
              key={a.id}
              onClick={() => setAba(a.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${aba === a.id ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {aba === 'pendentes' && (
          <div className="space-y-3">
            {carregandoPendentes ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
                Carregando...
              </p>
            ) : pendentes.length === 0 ? (
              <Card className="text-center py-12">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Nenhum ato aguardando confirmação.
                </p>
              </Card>
            ) : (
              pendentes.map((ato) => (
                <Card key={ato.id} className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p
                        className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                        translate="no"
                      >
                        {ato.aluno_nome}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {ORIGEM_LABEL[ato.origem]} · {nomePrincipio(ato.principio)} ·{' '}
                        {formatarData(ato.created_at)}
                      </p>
                    </div>
                  </div>
                  {ato.origem === 'missao' && ato.missao_titulo && (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      "{ato.missao_titulo}"
                    </p>
                  )}
                  {ato.origem === 'indicacao_colega' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Indicado por{' '}
                      <span className="font-medium" translate="no">
                        {ato.indicador_nome}
                      </span>
                    </p>
                  )}
                  {ato.texto && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3">
                      {ato.texto}
                    </p>
                  )}
                  <Input
                    size="sm"
                    placeholder="Comentário (opcional)"
                    value={comentarios[ato.id] || ''}
                    onChange={(e) =>
                      setComentarios((prev) => ({ ...prev, [ato.id]: e.target.value }))
                    }
                  />
                  {podeEditar && (
                    <div className="flex gap-2">
                      <Button
                        variant="success"
                        icon={Check}
                        disabled={processando === ato.id}
                        onClick={() => confirmarAto(ato.id, true)}
                      >
                        Aprovar
                      </Button>
                      <Button
                        variant="danger"
                        icon={X}
                        disabled={processando === ato.id}
                        onClick={() => confirmarAto(ato.id, false)}
                      >
                        Rejeitar
                      </Button>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        )}

        {aba === 'reconhecer' && (
          <Card>
            <form onSubmit={enviarReconhecimento} className="space-y-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Inscrito
                </label>
                {alunoSelecionado ? (
                  <div className="flex items-center justify-between px-3 h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <span className="text-sm text-gray-900 dark:text-gray-100" translate="no">
                      {alunoSelecionado.nome}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setAlunoSelecionado(null);
                        setBuscaAluno('');
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      icon={Search}
                      placeholder="Busque o nome do inscrito..."
                      value={buscaAluno}
                      onChange={(e) => setBuscaAluno(e.target.value)}
                    />
                    {opcoesAluno.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                        {opcoesAluno.map((a) => (
                          <button
                            type="button"
                            key={a.id}
                            onClick={() => setAlunoSelecionado(a)}
                            className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                            translate="no"
                          >
                            {a.nome}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Select
                label="Princípio"
                value={formReconhecer.principio}
                onChange={(e) => setFormReconhecer((f) => ({ ...f, principio: e.target.value }))}
                options={[
                  { value: '', label: 'Selecione...' },
                  ...principios.map((p) => ({ value: p.id, label: p.nome })),
                ]}
              />
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                  O que você observou
                </label>
                <textarea
                  value={formReconhecer.texto}
                  onChange={(e) => setFormReconhecer((f) => ({ ...f, texto: e.target.value }))}
                  rows={3}
                  placeholder="Ex.: Foi honesto ao admitir um erro em sala, sem que ninguém tivesse percebido."
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
                />
              </div>
              {podeCriar && (
                <Button
                  type="submit"
                  variant="primary"
                  icon={Award}
                  disabled={enviandoReconhecimento}
                  className="w-full"
                >
                  {enviandoReconhecimento ? 'Registrando...' : 'Registrar reconhecimento'}
                </Button>
              )}
            </form>
          </Card>
        )}

        {aba === 'missoes' && podeGerenciarMissoes && (
          <div className="space-y-3">
            {podeCriar && (
              <div className="flex justify-end">
                <Button icon={Plus} onClick={abrirNovaMissao}>
                  Nova Missão
                </Button>
              </div>
            )}
            {carregandoMissoes ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-12">
                Carregando...
              </p>
            ) : missoes.length === 0 ? (
              <Card className="text-center py-12">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  Nenhuma missão cadastrada ainda.
                </p>
              </Card>
            ) : (
              missoes.map((m) => (
                <Card
                  key={m.id}
                  className={`flex items-start justify-between gap-3 ${!m.ativa ? 'opacity-60' : ''}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {m.titulo}
                    </p>
                    {m.descricao && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.descricao}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-1 uppercase tracking-wide">
                      {nomePrincipio(m.principio)} · {m.ativa ? 'Ativa' : 'Inativa'}
                    </p>
                  </div>
                  {podeEditar && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => abrirEditarMissao(m)}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <Button variant="secondary" size="sm" onClick={() => alternarAtiva(m)}>
                        {m.ativa ? 'Desativar' : 'Reativar'}
                      </Button>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        )}
      </main>

      <Modal
        isOpen={modalMissaoAberto}
        onClose={() => setModalMissaoAberto(false)}
        title={missaoEditando ? 'Editar missão' : 'Nova missão de caráter'}
        maxWidth="md"
      >
        <form onSubmit={salvarMissao} className="space-y-4">
          <Input
            label="Título"
            value={formMissao.titulo}
            onChange={(e) => setFormMissao((f) => ({ ...f, titulo: e.target.value }))}
            required
            autoFocus
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Descrição
            </label>
            <textarea
              value={formMissao.descricao}
              onChange={(e) => setFormMissao((f) => ({ ...f, descricao: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
            />
          </div>
          <Select
            label="Princípio"
            value={formMissao.principio}
            onChange={(e) => setFormMissao((f) => ({ ...f, principio: e.target.value }))}
            options={[
              { value: '', label: 'Selecione...' },
              ...principios.map((p) => ({ value: p.id, label: p.nome })),
            ]}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalMissaoAberto(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={salvandoMissao}>
              {salvandoMissao ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default CaraterAdmin;
